import { useState } from 'react'
import type { FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { CheckCircle2, Eye, EyeOff, ShieldCheck } from 'lucide-react'
import { signup } from '~/api/auth'
import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'
import { Input } from '~/components/ui/input'

/** 좌측 히어로에 노출하는 가입 안내 포인트. */
const HIGHLIGHTS = [
    '대화형 진단으로 인증 준비 상태 점검',
    '항목별 자동 채점 · AI 보고서',
    '인증 취득 후 변경사항 유지 관리',
]

/**
 * 회원가입 신청 페이지 — 로그인과 동일한 스플릿 히어로 톤.
 * 제출 시 POST /auth/signup → 토큰을 저장하지 않고(자동 로그인 안 함)
 * "관리자 승인 후 로그인 가능" 안내만 보여준다.
 */
export function SignupPage() {
    const navigate = useNavigate()

    const [email, setEmail] = useState('')
    const [name, setName] = useState('')
    const [password, setPassword] = useState('')
    const [confirm, setConfirm] = useState('')
    const [showPw, setShowPw] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [done, setDone] = useState(false)

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault()
        setError(null)
        if (password.length < 8) {
            setError('비밀번호는 8자 이상이어야 해요.')
            return
        }
        if (password !== confirm) {
            setError('비밀번호가 일치하지 않아요.')
            return
        }
        setLoading(true)
        try {
            // 토큰을 발급하지 않으므로 store에 저장하거나 자동 로그인하지 않는다.
            await signup({ email, name, password })
            setDone(true)
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : '회원가입 신청에 실패했어요.'
            )
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className='grid h-screen grid-cols-1 overflow-hidden lg:grid-cols-[1.05fr_0.95fr]'>
            {/* ── 좌측 히어로 ── */}
            <section className='bg-navy relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:flex xl:p-14'>
                <div
                    className='pointer-events-none absolute inset-0 opacity-[0.13]'
                    style={{
                        backgroundImage:
                            'radial-gradient(rgba(255,255,255,.95) 1px, transparent 1.5px)',
                        backgroundSize: '30px 30px',
                    }}
                />

                <div className='relative flex items-center gap-2.5'>
                    <img
                        src='/brand/logo-white.svg'
                        alt=''
                        className='size-8'
                    />
                    <span className='text-lg font-bold tracking-tight'>
                        HACCP AI
                    </span>
                </div>

                <div className='relative'>
                    <p className='text-xs font-bold tracking-[0.14em] text-white/70 uppercase'>
                        식품안전 인증 지원 서비스
                    </p>
                    <h1 className='mt-4 text-4xl leading-tight font-extrabold tracking-tight xl:text-5xl'>
                        HACCP 인증,
                        <br />
                        지금 시작하세요
                    </h1>
                    <ul className='mt-8 space-y-3'>
                        {HIGHLIGHTS.map((label) => (
                            <li
                                key={label}
                                className='flex items-center gap-2.5 text-sm text-white/85'>
                                <span className='text-primary grid size-5 place-items-center rounded-full bg-white'>
                                    <ShieldCheck className='size-3' />
                                </span>
                                {label}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='relative text-xs tracking-wide text-white/55'>
                    PAI · HACCP AI
                </div>
            </section>

            {/* ── 우측 폼 ── */}
            <section className='bg-background flex items-center justify-center overflow-y-auto p-6'>
                <div className='animate-in fade-in w-full max-w-sm duration-500'>
                    {/* 모바일 브랜드 */}
                    <div className='mb-8 flex items-center gap-2 lg:hidden'>
                        <img
                            src='/brand/logo-icon.svg'
                            alt=''
                            className='size-9 rounded-lg'
                        />
                        <span className='text-foreground text-lg font-bold'>
                            HACCP AI
                        </span>
                    </div>

                    {done ? (
                        <div className='animate-in fade-in space-y-5 duration-500'>
                            <div className='text-success bg-success-subtle grid size-12 place-items-center rounded-full'>
                                <CheckCircle2 className='size-6' />
                            </div>
                            <div>
                                <h2 className='text-foreground text-2xl font-bold tracking-tight'>
                                    가입 신청 완료
                                </h2>
                                <p className='text-muted-foreground mt-2 text-sm leading-relaxed'>
                                    관리자 승인 후 로그인할 수 있어요. 승인까지
                                    잠시만 기다려 주세요.
                                </p>
                            </div>
                            <Button
                                type='button'
                                size='lg'
                                className='w-full'
                                onClick={() => navigate('/login')}>
                                로그인으로 돌아가기
                            </Button>
                        </div>
                    ) : (
                        <>
                            <h2 className='text-foreground text-2xl font-bold tracking-tight'>
                                회원가입 신청
                            </h2>
                            <p className='text-muted-foreground mt-1.5 text-sm'>
                                신청 후 관리자 승인을 거쳐 로그인할 수 있어요.
                            </p>

                            <form
                                onSubmit={handleSubmit}
                                className='mt-7 space-y-4'>
                                <div className='space-y-1.5'>
                                    <label
                                        className='text-foreground text-sm font-medium'
                                        htmlFor='signup-email'>
                                        이메일
                                    </label>
                                    <Input
                                        id='signup-email'
                                        type='email'
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        autoComplete='email'
                                        placeholder='name@example.com'
                                        required
                                    />
                                </div>
                                <div className='space-y-1.5'>
                                    <label
                                        className='text-foreground text-sm font-medium'
                                        htmlFor='signup-name'>
                                        이름
                                    </label>
                                    <Input
                                        id='signup-name'
                                        type='text'
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        autoComplete='name'
                                        placeholder='홍길동'
                                        required
                                    />
                                </div>
                                <div className='space-y-1.5'>
                                    <label
                                        className='text-foreground text-sm font-medium'
                                        htmlFor='signup-password'>
                                        비밀번호
                                    </label>
                                    <div className='relative'>
                                        <Input
                                            id='signup-password'
                                            type={showPw ? 'text' : 'password'}
                                            value={password}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                            autoComplete='new-password'
                                            placeholder='8자 이상'
                                            className='pr-10'
                                            required
                                        />
                                        <button
                                            type='button'
                                            tabIndex={-1}
                                            onClick={() => setShowPw((v) => !v)}
                                            aria-label={
                                                showPw
                                                    ? '비밀번호 숨기기'
                                                    : '비밀번호 표시'
                                            }
                                            className='text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 grid w-10 place-items-center transition-colors'>
                                            {showPw ? (
                                                <EyeOff className='size-4' />
                                            ) : (
                                                <Eye className='size-4' />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <div className='space-y-1.5'>
                                    <label
                                        className='text-foreground text-sm font-medium'
                                        htmlFor='signup-confirm'>
                                        비밀번호 확인
                                    </label>
                                    <Input
                                        id='signup-confirm'
                                        type={showPw ? 'text' : 'password'}
                                        value={confirm}
                                        onChange={(e) =>
                                            setConfirm(e.target.value)
                                        }
                                        autoComplete='new-password'
                                        placeholder='비밀번호 재입력'
                                        required
                                    />
                                </div>

                                {error ? (
                                    <ErrorBanner>{error}</ErrorBanner>
                                ) : null}

                                <Button
                                    type='submit'
                                    size='lg'
                                    className='w-full'
                                    disabled={loading}>
                                    {loading ? '처리 중…' : '가입 신청'}
                                </Button>
                            </form>

                            <p className='text-muted-foreground mt-6 text-center text-sm'>
                                이미 계정이 있으신가요?{' '}
                                <Link
                                    to='/login'
                                    className='text-primary font-medium hover:underline'>
                                    로그인
                                </Link>
                            </p>
                        </>
                    )}
                </div>
            </section>
        </div>
    )
}
